export const MESH_EXTENSIONS = [
  ".stl",
  ".obj",
  ".stp",
  ".step",
  ".iges",
  ".igs",
] as const;

export const COMPANION_EXTENSIONS = [
  ".mtl",
  ".jpg",
  ".jpeg",
  ".png",
  ".bmp",
  ".gif",
  ".tga",
] as const;

export const PREVIEWABLE_EXTENSIONS = new Set([".stl", ".obj"]);

/** CAD formats converted to STL on the server before preview. */
export const SERVER_PREVIEW_EXTENSIONS = new Set([".stp", ".step", ".iges", ".igs"]);

export function needsServerPreview(name: string): boolean {
  return SERVER_PREVIEW_EXTENSIONS.has(normalizeExtension(fileExtension(name)));
}

export function hasClientPreview(name: string): boolean {
  return PREVIEWABLE_EXTENSIONS.has(fileExtension(name));
}

const EXTENSION_ALIASES: Record<string, string> = {
  ".step": ".stp",
  ".igs": ".iges",
};

export function normalizeExtension(ext: string): string {
  const lower = ext.toLowerCase().trim();
  const withDot = lower.startsWith(".") ? lower : `.${lower}`;
  return EXTENSION_ALIASES[withDot] ?? withDot;
}

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
}

export function fileBaseName(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(0, dot).toLowerCase() : name.toLowerCase();
}

export function isMeshFile(
  name: string,
  allowed: readonly string[] = MESH_EXTENSIONS,
): boolean {
  const ext = normalizeExtension(fileExtension(name));
  const normalizedAllowed = new Set(allowed.map((item) => normalizeExtension(item)));
  return normalizedAllowed.has(ext);
}

export function isCompanionFile(name: string): boolean {
  return (COMPANION_EXTENSIONS as readonly string[]).includes(fileExtension(name));
}

export function acceptAttribute(allowed: readonly string[] = MESH_EXTENSIONS): string {
  const mesh = new Set<string>();
  for (const ext of allowed) {
    const norm = normalizeExtension(ext);
    mesh.add(norm);
    if (norm === ".stp") {
      mesh.add(".stp");
      mesh.add(".step");
    }
    if (norm === ".iges") {
      mesh.add(".iges");
      mesh.add(".igs");
    }
  }
  const meshList = [...mesh, ...COMPANION_EXTENSIONS].join(",");
  const mimes =
    "model/stl,model/obj,model/step,model/iges,image/jpeg,image/png,image/bmp,image/gif";
  return `${meshList},${mimes}`;
}

export function formatListLabel(allowed: readonly string[] = MESH_EXTENSIONS): string {
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const ext of allowed) {
    const norm = normalizeExtension(ext);
    if (seen.has(norm)) continue;
    seen.add(norm);
    if (norm === ".stp") {
      labels.push("STP", "STEP");
    } else if (norm === ".iges") {
      labels.push("IGES", "IGS");
    } else {
      labels.push(norm.slice(1).toUpperCase());
    }
  }
  return labels.join(", ");
}
